import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { useAptTickets } from '@/features/apt-counter/useAptTickets';
import { humaniseIssueError } from '@/features/inventory/issue-lines/issueErrors';
import { usePatient } from '@/features/patients/usePatients';
import { VisitForm, type VisitFormValues } from '@/features/visits/VisitForm';
import { useRecordVisit } from '@/features/visits/useVisits';
import { translateApiError } from '@/lib/apiError';

/**
 * Record one visit for one patient. Reached from the visits page with the
 * patient in the path and, when they took a number, the ticket in `?ticket=`.
 */
export function RecordVisitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patientId = '' } = useParams();
  const [search] = useSearchParams();
  const ticketId = search.get('ticket') ?? undefined;

  const patientQ = usePatient(patientId);
  const ticketsQ = useAptTickets();
  const record = useRecordVisit(patientId);

  // Chosen once for the life of this form and sent with every attempt: if a
  // save goes through but its response is lost, the retry carries the same id
  // and the server refuses it instead of dispensing the medicines twice.
  const [visitId] = useState(() => crypto.randomUUID());
  const [saved, setSaved] = useState<string | null>(null);

  if (patientQ.isLoading) {
    return <p className="py-10 text-center text-[13px] text-muted">{t('common.loading')}</p>;
  }
  if (patientQ.isError || !patientQ.data) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <p role="alert" className="text-[13.5px] text-destructive">
          {translateApiError(patientQ.error, { fallback: t('patients.couldNotLoadMsg') })}
        </p>
        <Button className="mt-4" variant="secondary" onClick={() => navigate('/visits')}>
          {t('visits.backToVisits')}
        </Button>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-12 text-center">
        <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-success-bg text-success">
          <Icon name="check" size={36} strokeWidth={2.2} />
        </div>
        <h2 className="mt-5 text-[22px] font-semibold tracking-[-0.02em]">{t('visits.recorded')}</h2>
        <p className="mt-1 max-w-[300px] text-[13.5px] leading-[1.5] text-muted">
          {t('visits.recordedBody', { name: patientQ.data.fullName })}
        </p>
        <div className="mt-6 flex w-full flex-col gap-2.5">
          <Button size="lg" full onClick={() => navigate('/visits')}>
            <Icon name="arrow_up_right" size={18} /> {t('visits.nextPatient')}
          </Button>
          <Button size="lg" full variant="ghost" onClick={() => navigate(`/visits/${saved}`)}>
            <Icon name="edit" size={16} /> {t('visits.editThisVisit')}
          </Button>
        </div>
      </div>
    );
  }

  const ticket = ticketId
    ? ticketsQ.data?.tickets.find((x) => x.id === ticketId)
    : undefined;

  const onSubmit = async (values: VisitFormValues) => {
    try {
      const visit = await record.mutateAsync({
        id: visitId,
        ...(ticketId ? { ticketId } : {}),
        ...(values.visitDate ? { visitDate: values.visitDate } : {}),
        ...(values.diagnosisNote ? { diagnosisNote: values.diagnosisNote } : {}),
        outsideMedicines: values.outsideMedicines,
        issued: values.added,
      });
      setSaved(visit.id);
    } catch {
      // Captured into record.error and rendered by the form.
    }
  };

  return (
    <VisitForm
      mode={{ kind: 'new' }}
      patient={patientQ.data}
      ticketNumber={ticket?.number}
      saving={record.isPending}
      error={record.isError ? humaniseIssueError(record.error, t) : null}
      onSubmit={onSubmit}
      onCancel={() => navigate('/visits')}
    />
  );
}
